// Учёт переходов по публичным страницам внутри приложения.
//
// ─────────────────────────────────────────────────────────────────────────────
// Зачем нужен клиентский канал, если есть серверный
// ─────────────────────────────────────────────────────────────────────────────
// Серверный хук SEO-раздачи видит только полные загрузки документа. Переход
// `/catalog` → `/catalog/{slug}` внутри одностраничного приложения запроса к
// серверу не делает вовсе, а в Capacitor сервер не видит даже первый экран:
// документ там поднимается из локального бандла. Без этого модуля обе
// поверхности недосчитывались бы систематически.
//
// ─────────────────────────────────────────────────────────────────────────────
// Первая локация: в вебе пропускаем, в приложении считаем
// ─────────────────────────────────────────────────────────────────────────────
// Различие определяется не догадкой «была ли это первая загрузка», а фактом:
// `injectHead` врезает в серверный документ `<meta name="sh-served-by">`. Есть
// маркер — заход уже записан сервером, повторять нельзя. Нет маркера (Capacitor,
// аварийный фолбэк Caddy на статику, dev) — считаем сами, иначе потеряли бы
// ровно самый частый просмотр: первый экран каждого запуска приложения.
//
// ─────────────────────────────────────────────────────────────────────────────
// Что здесь НЕ делается
// ─────────────────────────────────────────────────────────────────────────────
// Не копится офлайн-очередь: просмотр, доехавший через три дня со стухшим
// временем, отравляет временной ряд сильнее, чем помогает его наличие. Не
// присылается классификация (тип страницы, канал, устройство) — её считает
// сервер, доверять разметке из браузера нельзя. Не пишется ничего в cookie или
// localStorage: посетитель опознаётся серверным суточным хешем без следа на
// устройстве.

import { classifyPublicPath } from "@studyhub/shared/utils/public-path";
import { isProtectedPath } from "@/lib/protected-routes";

/** Адрес приёмника. В веб-сборке `VITE_API_URL` пуст → same-origin. */
const ENDPOINT = `${import.meta.env.VITE_API_URL ?? ""}/api/public/hit`;

/** Окно, в котором тот же адрес повторно не отправляется. */
const DEDUP_WINDOW_MS = 2_000;

let lastKey: string | null = null;
let lastSentAt = 0;
/** Локация, которую уже посчитал сервер, — её пропускаем ровно один раз. */
let skipLocation: string | null = null;
let initialized = false;

/** Отдал ли документ серверный SEO-роут. */
function servedBySeo(): boolean {
	if (typeof document === "undefined") return false;
	return document.querySelector('meta[name="sh-served-by"]') !== null;
}

/**
 * Текущий адрес — из `window.location`, а не из роутера.
 *
 * Роутер создан с `createLocalePrefixRewrite()`, который СНИМАЕТ языковой
 * префикс до сопоставления: у него `/de/catalog/x` выглядит как `/catalog/x`.
 * Взяв путь оттуда, мы бы схлопнули все 21 языковую версию в английскую и
 * потеряли разбивку по языкам целиком.
 */
function currentLocation(): string {
	return `${window.location.pathname}${window.location.search}`;
}

function send(path: string, referrer: string | undefined): void {
	const body = JSON.stringify({ path, referrer });
	try {
		// `text/plain` — CORS-safelisted тип: с `application/json` запрос стал бы
		// preflight'ным, а preflight `sendBeacon` отправить не умеет.
		const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
		if (navigator.sendBeacon?.(ENDPOINT, blob)) return;
	} catch {
		// Провалимся на fetch ниже.
	}
	void fetch(ENDPOINT, {
		method: "POST",
		body,
		headers: { "Content-Type": "text/plain;charset=UTF-8" },
		// Учёт не имеет права влиять на сессию: cookie не шлём вовсе.
		credentials: "omit",
		keepalive: true,
	}).catch(() => {
		// Офлайн или блокировщик — теряем событие молча и не копим очередь.
	});
}

/**
 * Записать просмотр текущей страницы.
 *
 * Вызывается на каждом разрешённом переходе роутера. Все проверки внутри:
 * вызывающей стороне не нужно знать ни про публичную зону, ни про дедупликацию.
 */
export function recordPublicPageView(): void {
	try {
		if (typeof window === "undefined") return;

		const location = currentLocation();

		// Ровно одна локация пропускается — та, что пришла серверным документом.
		if (skipLocation !== null) {
			const skipped = skipLocation;
			skipLocation = null;
			if (skipped === location) return;
		}

		const pathname = window.location.pathname;
		// Закрытая зона не считается вовсе — ни здесь, ни на сервере.
		if (isProtectedPath(pathname)) return;
		if (classifyPublicPath(location) === null) return;

		const now = Date.now();
		// Схлопывает повторные резолвы роутера, двойной эффект StrictMode и
		// возврат из bfcache. Смена фильтра каталога — тоже не новый просмотр.
		if (lastKey === location && now - lastSentAt < DEDUP_WINDOW_MS) return;
		lastKey = location;
		lastSentAt = now;

		// Referrer шлём только для входа извне. У внутреннего перехода его нет —
		// сервер поставит канал «внутренний», и источник визита не размоется.
		const referrer =
			document.referrer.length > 0 && !document.referrer.startsWith(window.location.origin)
				? document.referrer
				: undefined;

		send(location, referrer);
	} catch {
		// Учёт посещаемости не имеет права ломать навигацию.
	}
}

/**
 * Включить учёт.
 *
 * Идемпотентна: повторный вызов (StrictMode, hot-reload) ничего не удваивает.
 */
export function initPublicTrafficTracking(): void {
	if (initialized || typeof window === "undefined") return;
	initialized = true;
	// Маркер читается один раз при старте: дальше документ не перезагружается, а
	// значит и «уже посчитанная сервером» локация может быть только одна.
	skipLocation = servedBySeo() ? currentLocation() : null;
}
